import React, { useEffect, useState } from "react";
import PageHero from "../components/PageHero";
import SectionHeader from "../components/SectionHeader";
import Seo from "../components/Seo";
import { Download, Lock, FileText, ArrowRight } from "lucide-react";
import { api, fileUrl } from "../lib/api";
import { toast } from "sonner";
import useSiteSettings, { profilePdfUrl } from "../lib/useSiteSettings";

function PublicDocsList({ loading, items, onDownload }) {
  if (loading) return <div className="mt-6 text-[var(--melopa-muted)]">Loading…</div>;
  if (items.length === 0) {
    return (
      <div className="mt-6 card-premium p-6 text-[var(--melopa-muted)] text-sm">
        Public documents will be added soon. In the meantime, please {" "}
        <a className="text-navy underline" href="/contact">contact us</a> to request the company profile.
      </div>
    );
  }
  return (
    <div className="grid-cards mt-6" data-testid="public-docs">
      {items.map((d) => (
        <div key={d.id} className="card-premium p-5">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-lg bg-[#f1f5f9] grid place-items-center"><FileText className="w-5 h-5 text-navy" /></div>
            <div className="flex-1">
              <div className="font-semibold text-navy">{d.title}</div>
              <div className="text-xs text-[var(--melopa-muted)] mt-0.5">{d.category}</div>
              {d.description && <p className="text-sm text-[var(--melopa-muted)] mt-2">{d.description}</p>}
            </div>
          </div>
          <button onClick={() => onDownload(d)} className="btn-primary mt-4 w-full justify-center" data-testid={`download-${d.id}`}>
            <Download className="w-4 h-4" /> Download
          </button>
        </div>
      ))}
    </div>
  );
}

export default function Downloads() {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [requestModal, setRequestModal] = useState(null);
  const [reqForm, setReqForm] = useState({ name: "", organization: "", phone: "", email: "", purpose: "" });
  const settings = useSiteSettings();
  const pdf = profilePdfUrl(settings);

  useEffect(() => {
    api.get("/documents")
      .then((r) => setDocs(r.data.items || []))
      .catch(() => toast.error("Failed to load documents"))
      .finally(() => setLoading(false));
  }, []);

  const publicDocs = docs.filter((d) => d.visibility === "public");
  const protectedDocs = docs.filter((d) => d.visibility === "protected");

  const handleDownload = async (d) => {
    try {
      await api.post(`/documents/${d.id}/log-download`, {
        document_id: d.id,
      });
    } catch (err) {
      // Non-fatal: download tracking failed but the file should still open
      console.warn("Download log failed:", err?.message || err);
    }
    window.open(fileUrl(d.file_url), "_blank");
  };

  const submitRequest = async (e) => {
    e.preventDefault();
    try {
      await api.post("/document-requests", {
        document_id: requestModal?.id,
        document_title: requestModal?.title,
        ...reqForm,
      });
      toast.success("Request submitted. We will revert shortly.");
      setRequestModal(null);
      setReqForm({ name: "", organization: "", phone: "", email: "", purpose: "" });
    } catch (e) {
      toast.error("Failed to submit request");
    }
  };

  return (
    <div>
      <Seo title="Downloads" description="Company profile, brochures and vendor documents." path="/downloads" />
      <PageHero
        kicker="Downloads"
        title="Company Profile, Brochures & Vendor Documents"
        description="Download our public profile and category brochures. For protected vendor documents, please raise an access request."
        primary={pdf ? { href: pdf, label: "Download Company Profile" } : { to: "/submit", label: "Submit Requirement" }}
        secondary={{ to: "/contact", label: "Contact Us" }}
      />

      {pdf && (
        <section className="section-sm">
          <div className="container-x">
            <div className="card-premium p-5 md:p-6 flex flex-wrap md:flex-nowrap gap-4 items-center justify-between border-2 !border-[var(--melopa-gold)]">
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-12 h-12 rounded-lg bg-[#fef3c7] grid place-items-center shrink-0"><FileText className="w-6 h-6 text-[var(--melopa-saffron)]" /></div>
                <div className="min-w-0">
                  <div className="kicker">Featured</div>
                  <div className="font-bold text-navy text-base md:text-lg">Melopa Invents — Company Profile</div>
                  <div className="text-xs text-[var(--melopa-muted)]">Latest official company profile (PDF)</div>
                </div>
              </div>
              <a href={pdf} target="_blank" rel="noreferrer" className="btn-primary" data-testid="featured-pdf">
                <Download className="w-4 h-4" /> Download Profile
              </a>
            </div>
          </div>
        </section>
      )}

      <section className="section">
        <div className="container-x">
          <SectionHeader kicker="Public Downloads" title="Brochures & Profiles" />
          <PublicDocsList loading={loading} items={publicDocs} onDownload={handleDownload} />
        </div>
      </section>

      <section className="section bg-soft">
        <div className="container-x">
          <SectionHeader kicker="Protected / Vendor Documents" title="Request Access" subtitle="GST, MSME, FSSAI certificates, authorisation letters, bank details and tender documents are shared on request." />
          {protectedDocs.length === 0 ? (
            <div className="mt-6 card-premium p-6 text-[var(--melopa-muted)] text-sm">
              <div className="font-semibold text-navy mb-1">Available on Request</div>
              <ul className="list-tick mt-2">
                <li>GST Certificate</li>
                <li>MSME Certificate</li>
                <li>FSSAI Certificate</li>
                <li>Authorisation Letters</li>
                <li>Bank Details</li>
                <li>Tender / Empanelment Documents</li>
              </ul>
              <button
                onClick={() => setRequestModal({ id: null, title: "Vendor Documents Bundle" })}
                className="btn-gold mt-5"
                data-testid="request-vendor-bundle"
              >
                <Lock className="w-4 h-4" /> Request Vendor Documents <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          ) : (
            <div className="grid-cards mt-6" data-testid="protected-docs">
              {protectedDocs.map((d) => (
                <div key={d.id} className="card-premium p-5">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-lg bg-[#fef3c7] grid place-items-center"><Lock className="w-5 h-5 text-[var(--melopa-saffron)]" /></div>
                    <div className="flex-1">
                      <div className="font-semibold text-navy">{d.title}</div>
                      <div className="text-xs text-[var(--melopa-muted)] mt-0.5">{d.category}</div>
                      {d.description && <p className="text-sm text-[var(--melopa-muted)] mt-2">{d.description}</p>}
                    </div>
                  </div>
                  <button onClick={() => setRequestModal(d)} className="btn-gold mt-4 w-full justify-center" data-testid={`request-${d.id}`}>
                    <Lock className="w-4 h-4" /> Request Access
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {requestModal && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setRequestModal(null)}>
          <div className="bg-white rounded-2xl max-w-lg w-full p-6" onClick={(e) => e.stopPropagation()} data-testid="request-modal">
            <div className="kicker mb-1">Request Access</div>
            <h3 className="text-xl font-bold text-navy">{requestModal.title}</h3>
            <div className="divider-gold" />
            <form onSubmit={submitRequest} className="grid gap-3">
              <div>
                <label className="label">Full Name *</label>
                <input className="input" required value={reqForm.name} onChange={(e) => setReqForm({ ...reqForm, name: e.target.value })} data-testid="req-name" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="label">Organization</label>
                  <input className="input" value={reqForm.organization} onChange={(e) => setReqForm({ ...reqForm, organization: e.target.value })} />
                </div>
                <div>
                  <label className="label">Phone *</label>
                  <input className="input" required value={reqForm.phone} onChange={(e) => setReqForm({ ...reqForm, phone: e.target.value })} data-testid="req-phone" />
                </div>
              </div>
              <div>
                <label className="label">Email</label>
                <input className="input" type="email" value={reqForm.email} onChange={(e) => setReqForm({ ...reqForm, email: e.target.value })} />
              </div>
              <div>
                <label className="label">Purpose</label>
                <textarea className="input" rows={3} value={reqForm.purpose} onChange={(e) => setReqForm({ ...reqForm, purpose: e.target.value })} placeholder="e.g. Tender empanelment, vendor registration…" />
              </div>
              <div className="flex gap-2 justify-end">
                <button type="button" className="btn-outline" onClick={() => setRequestModal(null)}>Cancel</button>
                <button type="submit" className="btn-primary" data-testid="req-submit">Submit Request</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
